<template>
  <div>
    <head>
      <link rel="shortcut icon" href="assets/images/favicon.svg">
      <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />
      <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
    </head>

    <body @contextmenu.prevent data-sidebar="dark">
    <div id="layout-wrapper">
      <NavigationBar />

      <div id="content" class="content-minimize">
        <div class="page-content">
          <div class="container-fluid">
            <!-- Page Title -->
            <div class="row">
              <div class="col-12">
                <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                  <h4 class="mb-sm-0 font-size-18">{{ editMode ? 'Edit Patient' : 'Patient Registration' }}</h4>
                  <div class="page-title-right">
                    <ol class="breadcrumb m-0">
                      <li class="breadcrumb-item"><a href="javascript: void(0);">Hospital</a></li>
                      <li class="breadcrumb-item"><a href="javascript: void(0);" @click="goTo('patient-list')">Patients</a></li>
                      <li class="breadcrumb-item active">{{ editMode ? 'Edit' : 'Register' }}</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>

            <!-- Progress Steps -->
            <div class="row mb-4">
              <div class="col-12">
                <div class="card">
                  <div class="card-body py-3">
                    <div class="d-flex justify-content-between">
                      <div
                          v-for="(step, index) in steps"
                          :key="index"
                          class="step-item text-center flex-fill"
                          :class="{ 'active': currentStep === index, 'completed': currentStep > index }"
                          @click="goToStep(index)"
                          style="cursor: pointer;"
                      >
                        <div class="step-circle mx-auto mb-2">
                          <i v-if="currentStep > index" class="mdi mdi-check"></i>
                          <span v-else>{{ index + 1 }}</span>
                        </div>
                        <small>{{ step.title }}</small>
                      </div>
                    </div>
                    <div class="progress mt-3" style="height: 4px;">
                      <div
                          class="progress-bar bg-primary"
                          :style="{ width: ((currentStep / (steps.length - 1)) * 100) + '%' }"
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Registration Form -->
            <form @submit.prevent="submitForm">
              <!-- Step 1: Basic Information -->
              <div v-show="currentStep === 0" class="card mb-4">
                <div class="card-header">
                  <h5 class="card-title mb-0"><i class="mdi mdi-account me-2"></i>Basic Information</h5>
                </div>
                <div class="card-body">
                  <div class="row g-3">
                    <div class="col-md-4">
                      <label class="form-label">First Name <span class="text-danger">*</span></label>
                      <input type="text" class="form-control" v-model="form.firstName" :class="{ 'is-invalid': errors.firstName }" placeholder="Enter first name" required>
                      <div class="invalid-feedback">{{ errors.firstName }}</div>
                    </div>
                    <div class="col-md-4">
                      <label class="form-label">Middle Name</label>
                      <input type="text" class="form-control" v-model="form.middleName" placeholder="Enter middle name">
                    </div>
                    <div class="col-md-4">
                      <label class="form-label">Last Name <span class="text-danger">*</span></label>
                      <input type="text" class="form-control" v-model="form.lastName" :class="{ 'is-invalid': errors.lastName }" placeholder="Enter last name" required>
                      <div class="invalid-feedback">{{ errors.lastName }}</div>
                    </div>
                    <div class="col-md-4">
                      <label class="form-label">Gender <span class="text-danger">*</span></label>
                      <select class="form-select" v-model="form.gender" :class="{ 'is-invalid': errors.gender }" required>
                        <option value="">Select Gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                      </select>
                      <div class="invalid-feedback">{{ errors.gender }}</div>
                    </div>
                    <div class="col-md-4">
                      <label class="form-label">Date of Birth <span class="text-danger">*</span></label>
                      <input type="date" class="form-control" v-model="form.dateOfBirth" :class="{ 'is-invalid': errors.dateOfBirth }" :max="maxDate" required>
                      <div class="invalid-feedback">{{ errors.dateOfBirth }}</div>
                    </div>
                    <div class="col-md-4">
                      <label class="form-label">Age</label>
                      <div class="input-group">
                        <input type="text" class="form-control" :value="calculatedAge" readonly>
                        <span class="input-group-text">{{ ageGroup }}</span>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <label class="form-label">Blood Group</label>
                      <select class="form-select" v-model="form.bloodGroup">
                        <option value="">Select Blood Group</option>
                        <option v-for="bg in ['A+','A-','B+','B-','AB+','AB-','O+','O-']" :key="bg" :value="bg">{{ bg }}</option>
                      </select>
                    </div>
                    <div class="col-md-4">
                      <label class="form-label">Marital Status</label>
                      <select class="form-select" v-model="form.maritalStatus">
                        <option value="">Select Status</option>
                        <option v-for="s in ['Single','Married','Divorced','Widowed','Separated']" :key="s" :value="s">{{ s }}</option>
                      </select>
                    </div>
                    <div class="col-md-4">
                      <label class="form-label">Occupation</label>
                      <input type="text" class="form-control" v-model="form.occupation" placeholder="Enter occupation">
                    </div>
                    <div class="col-md-4">
                      <label class="form-label">Nationality</label>
                      <input type="text" class="form-control" v-model="form.nationality" placeholder="Enter nationality">
                    </div>
                    <div class="col-md-4">
                      <label class="form-label">Religion</label>
                      <select class="form-select" v-model="form.religion">
                        <option value="">Select Religion</option>
                        <option v-for="r in ['Christian','Muslim','Hindu','Traditional','Other','None']" :key="r" :value="r">{{ r }}</option>
                      </select>
                    </div>
                    <div class="col-md-4">
                      <label class="form-label">Ethnicity</label>
                      <input type="text" class="form-control" v-model="form.ethnicity" placeholder="Enter ethnicity">
                    </div>
                  </div>
                </div>
              </div>

              <!-- Step 2: Identification -->
              <div v-show="currentStep === 1" class="card mb-4">
                <div class="card-header">
                  <h5 class="card-title mb-0"><i class="mdi mdi-card-account-details me-2"></i>Identification Documents</h5>
                </div>
                <div class="card-body">
                  <div class="row g-3">
                    <div class="col-md-6">
                      <label class="form-label">National ID Number</label>
                      <input type="text" class="form-control" v-model="form.idNo" placeholder="Enter ID number" @blur="checkDuplicate('idNo')">
                      <small v-if="duplicateWarning.idNo" class="text-warning"><i class="mdi mdi-alert me-1"></i>{{ duplicateWarning.idNo }}</small>
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">Passport Number</label>
                      <input type="text" class="form-control" v-model="form.passportNo" placeholder="Enter passport number">
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">Birth Registration Number</label>
                      <input type="text" class="form-control" v-model="form.birthCertNo" placeholder="For minors">
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">Huduma Namba</label>
                      <input type="text" class="form-control" v-model="form.hudumaNamba" placeholder="Enter Huduma number">
                    </div>
                  </div>
                </div>
              </div>

              <!-- Step 3: Contact Information -->
              <div v-show="currentStep === 2" class="card mb-4">
                <div class="card-header">
                  <h5 class="card-title mb-0"><i class="mdi mdi-phone me-2"></i>Contact Information</h5>
                </div>
                <div class="card-body">
                  <div class="row g-3">
                    <div class="col-md-4">
                      <label class="form-label">Phone Number <span class="text-danger">*</span></label>
                      <input type="tel" class="form-control" v-model="form.phoneNumber" :class="{ 'is-invalid': errors.phoneNumber }" placeholder="07XXXXXXXX" @blur="checkDuplicate('phone')" required>
                      <div class="invalid-feedback">{{ errors.phoneNumber }}</div>
                      <small v-if="duplicateWarning.phone" class="text-warning"><i class="mdi mdi-alert me-1"></i>{{ duplicateWarning.phone }}</small>
                    </div>
                    <div class="col-md-4">
                      <label class="form-label">Alternative Phone</label>
                      <input type="tel" class="form-control" v-model="form.alternativePhone" placeholder="07XXXXXXXX">
                    </div>
                    <div class="col-md-4">
                      <label class="form-label">Email Address</label>
                      <input type="email" class="form-control" v-model="form.email" placeholder="email@example.com">
                    </div>
                    <div class="col-12"><hr class="my-2"><h6 class="text-muted">Physical Address</h6></div>
                    <div class="col-md-6">
                      <label class="form-label">Physical Address</label>
                      <textarea class="form-control" v-model="form.physicalAddress" rows="2" placeholder="Enter physical address"></textarea>
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">Postal Address</label>
                      <input type="text" class="form-control" v-model="form.postalAddress" placeholder="P.O Box XXX">
                    </div>
                    <div class="col-md-4">
                      <label class="form-label">County</label>
                      <input type="text" class="form-control bg-light" v-model="county.countyName" readonly>
                    </div>
                    <div class="col-md-4">
                      <label class="form-label">Sub-County</label>
                      <select class="form-select" v-model="selectedSubCounty" @change="onSubCountyChange">
                        <option value="">Select Sub-County</option>
                        <option v-for="sc in subCounties" :key="sc.subCountyID" :value="sc">{{ sc.subCountyName }}</option>
                      </select>
                    </div>
                    <div class="col-md-4">
                      <label class="form-label">Ward</label>
                      <select class="form-select" v-model="selectedWard" @change="onWardChange" :disabled="!selectedSubCounty">
                        <option value="">Select Ward</option>
                        <option v-for="w in wards" :key="w.wardID" :value="w">{{ w.wardName }}</option>
                      </select>
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">Village/Estate</label>
                      <input type="text" class="form-control" v-model="form.village" placeholder="Enter village or estate">
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">Landmark</label>
                      <input type="text" class="form-control" v-model="form.landmark" placeholder="Near...">
                    </div>
                  </div>
                </div>
              </div>

              <!-- Step 4: Next of Kin -->
              <div v-show="currentStep === 3" class="card mb-4">
                <div class="card-header">
                  <h5 class="card-title mb-0"><i class="mdi mdi-account-group me-2"></i>Next of Kin / Emergency Contact</h5>
                </div>
                <div class="card-body">
                  <div class="row g-3">
                    <div class="col-12"><h6 class="text-primary">Next of Kin</h6></div>
                    <div class="col-md-4">
                      <label class="form-label">Full Name</label>
                      <input type="text" class="form-control" v-model="form.nextOfKinName" placeholder="Enter full name">
                    </div>
                    <div class="col-md-4">
                      <label class="form-label">Relationship</label>
                      <select class="form-select" v-model="form.nextOfKinRelationship">
                        <option value="">Select Relationship</option>
                        <option v-for="r in ['Spouse','Parent','Child','Sibling','Guardian','Friend','Other']" :key="r" :value="r">{{ r }}</option>
                      </select>
                    </div>
                    <div class="col-md-4">
                      <label class="form-label">Phone Number</label>
                      <input type="tel" class="form-control" v-model="form.nextOfKinPhone" placeholder="07XXXXXXXX">
                    </div>
                    <div class="col-md-4">
                      <label class="form-label">ID Number</label>
                      <input type="text" class="form-control" v-model="form.nextOfKinIDNo" placeholder="Enter ID number">
                    </div>
                    <div class="col-md-8">
                      <label class="form-label">Address</label>
                      <input type="text" class="form-control" v-model="form.nextOfKinAddress" placeholder="Enter address">
                    </div>
                    <div class="col-12"><hr class="my-2"><h6 class="text-primary">Emergency Contact <button type="button" class="btn btn-sm btn-soft-secondary ms-2" @click="copyNextOfKin"><i class="mdi mdi-content-copy me-1"></i>Same as Next of Kin</button></h6></div>
                    <div class="col-md-4">
                      <label class="form-label">Full Name</label>
                      <input type="text" class="form-control" v-model="form.emergencyContactName" placeholder="Enter full name">
                    </div>
                    <div class="col-md-4">
                      <label class="form-label">Phone Number</label>
                      <input type="tel" class="form-control" v-model="form.emergencyContactPhone" placeholder="07XXXXXXXX">
                    </div>
                    <div class="col-md-4">
                      <label class="form-label">Relationship</label>
                      <select class="form-select" v-model="form.emergencyContactRelationship">
                        <option value="">Select Relationship</option>
                        <option v-for="r in ['Spouse','Parent','Child','Sibling','Guardian','Friend','Other']" :key="r" :value="r">{{ r }}</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Step 5: Medical History -->
              <div v-show="currentStep === 4" class="card mb-4">
                <div class="card-header">
                  <h5 class="card-title mb-0"><i class="mdi mdi-heart-pulse me-2"></i>Medical History</h5>
                </div>
                <div class="card-body">
                  <div class="row g-3">
                    <div class="col-md-6">
                      <label class="form-label">Known Allergies</label>
                      <textarea class="form-control" v-model="form.allergies" rows="3" placeholder="List any known allergies"></textarea>
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">Chronic Conditions</label>
                      <textarea class="form-control" v-model="form.chronicConditions" rows="3" placeholder="List any chronic conditions"></textarea>
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">Disabilities</label>
                      <textarea class="form-control" v-model="form.disabilities" rows="3" placeholder="List any disabilities"></textarea>
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">Current Medications</label>
                      <textarea class="form-control" v-model="form.currentMedications" rows="3" placeholder="List current medications"></textarea>
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">Previous Surgeries</label>
                      <textarea class="form-control" v-model="form.previousSurgeries" rows="3" placeholder="List any previous surgeries"></textarea>
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">Family Medical History</label>
                      <textarea class="form-control" v-model="form.familyHistory" rows="3" placeholder="Relevant family medical history"></textarea>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Step 6: Review -->
              <div v-show="currentStep === 5" class="card mb-4">
                <div class="card-header">
                  <h5 class="card-title mb-0"><i class="mdi mdi-check-circle me-2"></i>Review & Submit</h5>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-6">
                      <div class="card bg-light mb-3">
                        <div class="card-header py-2"><strong>Personal Information</strong></div>
                        <div class="card-body py-2">
                          <p class="mb-1"><strong>Name:</strong> {{ fullName }}</p>
                          <p class="mb-1"><strong>Gender:</strong> {{ form.gender }}</p>
                          <p class="mb-1"><strong>DOB:</strong> {{ form.dateOfBirth }} ({{ calculatedAge }})</p>
                          <p class="mb-1"><strong>Blood Group:</strong> {{ form.bloodGroup || 'N/A' }}</p>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="card bg-light mb-3">
                        <div class="card-header py-2"><strong>Contact Information</strong></div>
                        <div class="card-body py-2">
                          <p class="mb-1"><strong>Phone:</strong> {{ form.phoneNumber }}</p>
                          <p class="mb-1"><strong>Email:</strong> {{ form.email || 'N/A' }}</p>
                          <p class="mb-1"><strong>County:</strong> {{ county.countyName || 'N/A' }}</p>
                          <p class="mb-1"><strong>Sub-County:</strong> {{ form.subCountyName || 'N/A' }}</p>
                          <p class="mb-1"><strong>Ward:</strong> {{ form.wardName || 'N/A' }}</p>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="card bg-light mb-3">
                        <div class="card-header py-2"><strong>Identification</strong></div>
                        <div class="card-body py-2">
                          <p class="mb-1"><strong>ID No:</strong> {{ form.idNo || 'N/A' }}</p>
                          <p class="mb-1"><strong>Passport:</strong> {{ form.passportNo || 'N/A' }}</p>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="card bg-light mb-3">
                        <div class="card-header py-2"><strong>Next of Kin</strong></div>
                        <div class="card-body py-2">
                          <p class="mb-1"><strong>Name:</strong> {{ form.nextOfKinName || 'N/A' }}</p>
                          <p class="mb-1"><strong>Phone:</strong> {{ form.nextOfKinPhone || 'N/A' }}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-if="form.allergies || form.chronicConditions" class="alert alert-warning">
                    <h6 class="alert-heading"><i class="mdi mdi-alert me-2"></i>Medical Alerts</h6>
                    <p v-if="form.allergies" class="mb-1"><strong>Allergies:</strong> {{ form.allergies }}</p>
                    <p v-if="form.chronicConditions" class="mb-0"><strong>Chronic Conditions:</strong> {{ form.chronicConditions }}</p>
                  </div>
                  <div class="form-check mb-3">
                    <input class="form-check-input" type="checkbox" v-model="createVisitAfter" id="createVisit">
                    <label class="form-check-label" for="createVisit">Create a new visit immediately after registration</label>
                  </div>
                </div>
              </div>

              <!-- Navigation Buttons -->
              <div class="card">
                <div class="card-body d-flex justify-content-between">
                  <button type="button" class="btn btn-secondary" @click="previousStep" :disabled="currentStep === 0">
                    <i class="mdi mdi-chevron-left me-1"></i> Previous
                  </button>
                  <div>
                    <button type="button" class="btn btn-outline-secondary me-2" @click="resetForm"><i class="mdi mdi-refresh me-1"></i> Reset</button>
                    <button v-if="currentStep < steps.length - 1" type="button" class="btn btn-primary" @click="nextStep">Next <i class="mdi mdi-chevron-right ms-1"></i></button>
                    <button v-else type="submit" class="btn btn-success" :disabled="loading">
                      <span v-if="loading"><span class="spinner-border spinner-border-sm me-1"></span>Registering...</span>
                      <span v-else><i class="mdi mdi-check me-1"></i>{{ editMode ? 'Update Patient' : 'Register Patient' }}</span>
                    </button>
                  </div>
                </div>
              </div>
            </form>

            <!-- Success Modal -->
            <div class="modal fade" id="successModal" tabindex="-1" ref="successModal">
              <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                  <div class="modal-body text-center py-5">
                    <div class="avatar-lg mx-auto mb-4">
                        <span class="avatar-title bg-success-subtle text-success rounded-circle">
                          <i class="mdi mdi-check-circle font-size-48"></i>
                        </span>
                    </div>
                    <h4 class="mb-3">Patient Registered Successfully!</h4>
                    <p class="text-muted mb-4">Patient ID: <strong class="text-primary">{{ registeredPatient?.patientID }}</strong></p>
                    <div class="d-flex justify-content-center gap-2">
                      <button class="btn btn-outline-secondary" @click="registerAnother"><i class="mdi mdi-plus me-1"></i> Register Another</button>
                      <button class="btn btn-primary" @click="viewPatient"><i class="mdi mdi-eye me-1"></i> View Patient</button>
                      <button v-if="createVisitAfter" class="btn btn-success" @click="goToCreateVisit"><i class="mdi mdi-calendar-plus me-1"></i> Create Visit</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        <footer class="footer">
          <div class="container-fluid">
            <div class="row">
              <div class="col-sm-6"></div>
              <div class="col-sm-6">
                <div class="text-sm-end d-sm-block">County Hospital Management System</div>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
    </body>
  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation.vue";
import { execute, hospital, biller, authUrl } from "@/api";

export default {
  name: "PatientRegistration",
  components: { NavigationBar },
  props: { patientId: { type: String, default: null } },

  data() {
    return {
      loading: false,
      editMode: false,
      currentStep: 0,
      createVisitAfter: false,
      registeredPatient: null,
      successModalInstance: null,
      steps: [
        { title: 'Basic Info' },
        { title: 'Identification' },
        { title: 'Contact' },
        { title: 'Next of Kin' },
        { title: 'Medical' },
        { title: 'Review' }
      ],
      form: {
        firstName: '', middleName: '', lastName: '', gender: '', dateOfBirth: '', bloodGroup: '', maritalStatus: '', occupation: '', nationality: 'Kenyan', religion: '', ethnicity: '',
        idNo: '', passportNo: '', birthCertNo: '', hudumaNamba: '',
        phoneNumber: '', alternativePhone: '', email: '', physicalAddress: '', postalAddress: '', county: '', subCountyID: '', subCountyName: '', wardID: '', wardName: '', village: '', landmark: '',
        nextOfKinName: '', nextOfKinRelationship: '', nextOfKinPhone: '', nextOfKinIDNo: '', nextOfKinAddress: '',
        emergencyContactName: '', emergencyContactPhone: '', emergencyContactRelationship: '',
        allergies: '', chronicConditions: '', disabilities: '', currentMedications: '', previousSurgeries: '', familyHistory: ''
      },
      errors: {},
      duplicateWarning: { idNo: '', phone: '' },

      // Dynamic data from API
      county: {
        countyID: '',
        countyName: '',
        countyNameFull: ''
      },
      subCounties: [],
      wards: [],
      selectedSubCounty: null,
      selectedWard: null
    };
  },

  computed: {
    maxDate() { return new Date().toISOString().split('T')[0]; },
    calculatedAge() {
      if (!this.form.dateOfBirth) return '';
      const today = new Date();
      const birth = new Date(this.form.dateOfBirth);
      let age = today.getFullYear() - birth.getFullYear();
      const m = today.getMonth() - birth.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
      if (age < 1) {
        const months = (today.getFullYear() - birth.getFullYear()) * 12 + (today.getMonth() - birth.getMonth());
        return `${months} months`;
      }
      return `${age} years`;
    },
    ageGroup() {
      if (!this.form.dateOfBirth) return '';
      const age = parseInt(this.calculatedAge);
      if (isNaN(age)) return 'Infant';
      if (age < 1) return 'Infant';
      if (age < 13) return 'Child';
      if (age < 20) return 'Adolescent';
      if (age < 60) return 'Adult';
      return 'Elderly';
    },
    fullName() { return [this.form.firstName, this.form.middleName, this.form.lastName].filter(n => n).join(' '); },
    facilityID() { return sessionStorage.getItem('hospitalFacilityID') || ''; },
    facilityName() { return sessionStorage.getItem('hospitalFacilityName') || ''; }
  },

  mounted() {
    if (this.patientId) { this.editMode = true; this.loadPatient(); }
    this.successModalInstance = new window.bootstrap.Modal(this.$refs.successModal);
    this.getCounty();
    this.getSubCounties();
  },

  methods: {
    goTo(route) { this.$router.push(route); },

    // ========== County/SubCounty/Ward API Methods ==========
    getCounty() {
      const data = new FormData();
      data.append("function", "getCounty");
      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.county = res.data.data.county;
              this.form.county = this.county.countyName;
            }
          })
          .catch((e) => {
            console.error("Error loading county:", e.message);
          });
    },

    getSubCounties() {
      const data = new FormData();
      data.append("function", "getSubCounty");
      execute(data, biller)
          .then((res) => {
            if (res.data.success) {
              this.subCounties = res.data.data.subCounties;
            }
          })
          .catch((e) => {
            console.error("Error loading sub-counties:", e.message);
          });
    },

    getWards() {
      if (!this.selectedSubCounty || !this.selectedSubCounty.subCountyID) {
        this.wards = [];
        return;
      }
      const data = new FormData();
      data.append("function", "getWards");
      data.append("subCountyID", this.selectedSubCounty.subCountyID);
      execute(data, biller)
          .then((res) => {
            if (res.data.success) {
              this.wards = res.data.data.wards;
            }
          })
          .catch((e) => {
            console.error("Error loading wards:", e.message);
          });
    },

    onSubCountyChange() {
      // Clear ward selection when sub-county changes
      this.selectedWard = null;
      this.wards = [];
      this.form.wardID = '';
      this.form.wardName = '';

      if (this.selectedSubCounty) {
        this.form.subCountyID = this.selectedSubCounty.subCountyID;
        this.form.subCountyName = this.selectedSubCounty.subCountyName;
        this.getWards();
      } else {
        this.form.subCountyID = '';
        this.form.subCountyName = '';
      }
    },

    onWardChange() {
      if (this.selectedWard) {
        this.form.wardID = this.selectedWard.wardID;
        this.form.wardName = this.selectedWard.wardName;
      } else {
        this.form.wardID = '';
        this.form.wardName = '';
      }
    },
    // ========== End County/SubCounty/Ward API Methods ==========

    loadPatient() {
      this.loading = true;
      const data = new FormData();
      data.append("function", "searchPatient");
      data.append("patientID", this.patientId);
      data.append("singleResult", "true");
      data.append("sessionNames", sessionStorage.getItem("sessionNames"));
      data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));
      execute(data, hospital).then((res) => {
        this.loading = false;
        if (res.data.success && res.data.data.patient) {
          Object.assign(this.form, res.data.data.patient);
          // Set selected subcounty and ward for dropdowns
          if (this.form.subCountyID && this.form.subCountyName) {
            this.selectedSubCounty = { subCountyID: this.form.subCountyID, subCountyName: this.form.subCountyName };
            this.getWards();
            // Set ward after wards are loaded
            setTimeout(() => {
              if (this.form.wardID && this.form.wardName) {
                this.selectedWard = { wardID: this.form.wardID, wardName: this.form.wardName };
              }
            }, 500);
          }
        }
      }).catch((e) => { this.loading = false; console.error('Error:', e); });
    },

    checkDuplicate(type) {
      const data = new FormData();
      data.append("function", "searchPatient");
      data.append("singleResult", "true");

      if (type === 'idNo' && this.form.idNo) {
        data.append("idNo", this.form.idNo);
      } else if (type === 'phone' && this.form.phoneNumber) {
        data.append("phone", this.form.phoneNumber);
      } else return;

      data.append("sessionNames", sessionStorage.getItem("sessionNames"));
      data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));
      execute(data, hospital).then((res) => {
        if (res.data.success && res.data.data.patient) {
          this.duplicateWarning[type === 'idNo' ? 'idNo' : 'phone'] = `Patient exists: ${res.data.data.patient.fullNames}`;
        } else {
          this.duplicateWarning[type === 'idNo' ? 'idNo' : 'phone'] = '';
        }
      }).catch(e => console.error('Error:', e));
    },

    copyNextOfKin() {
      this.form.emergencyContactName = this.form.nextOfKinName;
      this.form.emergencyContactPhone = this.form.nextOfKinPhone;
      this.form.emergencyContactRelationship = this.form.nextOfKinRelationship;
    },

    validateStep(step) {
      this.errors = {};
      if (step === 0) {
        if (!this.form.firstName) this.errors.firstName = 'First name is required';
        if (!this.form.lastName) this.errors.lastName = 'Last name is required';
        if (!this.form.gender) this.errors.gender = 'Gender is required';
        if (!this.form.dateOfBirth) this.errors.dateOfBirth = 'Date of birth is required';
      }
      if (step === 2) {
        if (!this.form.phoneNumber) this.errors.phoneNumber = 'Phone number is required';
        else if (!/^0[17]\d{8}$/.test(this.form.phoneNumber)) this.errors.phoneNumber = 'Invalid phone format';
      }
      return Object.keys(this.errors).length === 0;
    },

    goToStep(step) {
      for (let i = 0; i < step; i++) { if (!this.validateStep(i)) { this.currentStep = i; return; } }
      this.currentStep = step;
    },
    nextStep() { if (this.validateStep(this.currentStep)) this.currentStep++; },
    previousStep() { if (this.currentStep > 0) this.currentStep--; },

    submitForm() {
      if (!this.validateStep(0) || !this.validateStep(2)) return;
      this.loading = true;
      const data = new FormData();
      data.append("function", this.editMode ? "updatePatient" : "registerPatient");
      Object.keys(this.form).forEach(key => data.append(key, this.form[key] || ''));
      data.append("fullNames", this.fullName);
      data.append("age", parseInt(this.calculatedAge) || 0);
      data.append("ageGroup", this.ageGroup);
      data.append("registrationFacilityID", this.facilityID);
      data.append("registrationFacilityName", this.facilityName);
      data.append("sessionNames", sessionStorage.getItem("sessionNames"));
      data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));
      if (this.editMode) data.append("patientID", this.patientId);

      execute(data, hospital).then((res) => {
        this.loading = false;
        if (res.data.success) { this.registeredPatient = res.data.data.patient; this.successModalInstance.show(); }
        else alert(res.data.message || 'Registration failed');
      }).catch((e) => { this.loading = false; alert('An error occurred'); console.error('Error:', e); });
    },

    resetForm() {
      this.currentStep = 0;
      this.errors = {};
      this.duplicateWarning = { idNo: '', phone: '' };
      this.selectedSubCounty = null;
      this.selectedWard = null;
      this.wards = [];
      Object.keys(this.form).forEach(key => { this.form[key] = key === 'nationality' ? 'Kenyan' : (key === 'county' ? this.county.countyName : ''); });
    },
    registerAnother() { this.successModalInstance.hide(); this.resetForm(); },
    viewPatient() { this.successModalInstance.hide(); this.$router.push('/patient-profile/' + this.registeredPatient.patientID); },
    goToCreateVisit() { this.successModalInstance.hide(); this.$router.push('/new-visit?patientID=' + this.registeredPatient.patientID); }
  }
}
</script>

<style scoped>
.step-item { position: relative; }
.step-circle { width: 36px; height: 36px; border-radius: 50%; background: #e9ecef; display: flex; align-items: center; justify-content: center; font-weight: 600; color: #6c757d; transition: all 0.3s ease; }
.step-item.active .step-circle { background: #556ee6; color: white; }
.step-item.completed .step-circle { background: #34c38f; color: white; }
.step-item:not(:last-child)::after { content: ''; position: absolute; top: 18px; left: 55%; width: 90%; height: 2px; background: #e9ecef; z-index: -1; }
.step-item.completed:not(:last-child)::after { background: #34c38f; }
.avatar-lg { width: 5rem; height: 5rem; }
.avatar-title { display: flex; align-items: center; justify-content: center; width: 100%; height: 100%; }
.bg-success-subtle { background-color: rgba(52, 195, 143, 0.1); }
.btn-soft-secondary { background-color: rgba(116, 120, 141, 0.1); color: #74788d; border: none; }
.btn-soft-secondary:hover { background-color: #74788d; color: #fff; }
</style>